import {
  getCircleColor,
  getCircleColorBiologicals,
  getCircleColorNutrient,
} from "../../../shared/layout/Plots/utils";
import {
  calculatePlotAreaHa,
  calculateTotalAmount,
} from "../../../shared/utils/areaCalculation";
import { type ProtocolResponse } from "../types";

const toNumber = (value: unknown): number => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

/**
 * Safely derive the replicant count regardless of whether
 * plot.replicants is an Array, a keyed object map, null, or undefined.
 *
 *   Array  → use .length
 *   Object → use Object.keys().length
 *   Falsy  → 0
 *
 * Replicant multiplier examples:
 *   0 replicants → ×1  (plot only)
 *   1 replicant  → ×2  (plot + rep1)
 *   3 replicants → ×4  (plot + rep1 + rep2 + rep3)
 */
const getReplicantCount = (plot: any): number => {
  const replicants = plot?.replicants;
  if (!replicants) return 0;
  if (Array.isArray(replicants)) return replicants.length;
  if (typeof replicants === "object") return Object.keys(replicants).length;
  return 0;
};

const getSeedCalculatedTotals = (
  seedRatesAndDosages: any[] | undefined,
  rawProperties: any[] | undefined,
  measureUnit: string,
  replicateMultiplier = 1
): {
  by_product: Array<{
    variety: string;
    value: number;
    measure_unit: string;
    rate: number;
  }>;
} => {
  const totalsByProduct: Record<
    string,
    { value: number; measure_unit: string; rate: number }
  > = {};

  (seedRatesAndDosages ?? []).forEach((seedEntry: any, idx: number) => {
    const productName = seedEntry?.variety ?? "";
    if (!productName) return;

    if (!totalsByProduct[productName]) {
      totalsByProduct[productName] = {
        value: 0,
        measure_unit: measureUnit,
        rate: toNumber(seedEntry?.rate),
      };
    }

    // Prefer the pre-computed quantity on seedEntry (from updatedProperties).
    // If it is 0 (e.g. no plot geometry in tests), fall back to the raw
    // input quantity so that unit-tests that supply an explicit quantity
    // value still work correctly.
    const computedQty = toNumber(seedEntry?.quantity);
    const rawQty = toNumber(rawProperties?.[idx]?.quantity);
    const qty = computedQty !== 0 ? computedQty : rawQty;

    totalsByProduct[productName].value += qty * replicateMultiplier;
  });

  return {
    by_product: Object.entries(totalsByProduct).map(([variety, data]) => ({
      variety,
      value: data.value,
      measure_unit: data.measure_unit,
      rate: data.rate,
    })),
  };
};

const getFertiliserNutrientPercentage = (
  fertiliser: any,
  nutrientCode: string
): number => {
  const nutrients = Array.isArray(fertiliser?.nutrients)
    ? fertiliser.nutrients
    : [];

  const nutrient = nutrients.find(
    (item: any) => item?.nutrient_type === nutrientCode
  );

  return toNumber(nutrient?.percentage);
};

const getFertiliserCalculatedTotals = (
  fertiliserRatesAndDosages: any[] | undefined,
  standardFertilisers: any[] | undefined,
  nutrientCode: string,
  trialPlots: any[] = [],
  stripsData?: any,
  trialPlotIndex = 0,
  replicateMultiplier = 1
): {
  by_product: Array<{
    variety: string;
    value: number;
    measure_unit: string;
  }>;
  by_nutrient: Array<{ variety: string; value: number; measure_unit: string }>;
} => {
  const totalsByProduct: Record<
    string,
    { value: number; measure_unit: string }
  > = {};
  const totalsByNutrient: Record<
    string,
    { value: number; measure_unit: string }
  > = {};

  (fertiliserRatesAndDosages ?? []).forEach((fertiliserEntry: any) => {
    const applicationRate = toNumber(fertiliserEntry?.rate);
    const areaHa = calculatePlotAreaHa(
      fertiliserEntry?.plot_id,
      trialPlots,
      stripsData,
      trialPlotIndex
    );

    (standardFertilisers ?? []).forEach((fertiliser: any) => {
      const productName = fertiliser?.fertiliser;
      if (!productName) return;

      const productRatePerHa = toNumber(fertiliser?.rate);
      const productAmount = (applicationRate / 100) * productRatePerHa;
      const productMeasureUnit = "kg";

      // If areaHa is 0 (no geometry), use productAmount directly so that
      // unit-tests which do not supply plot geometry still get correct values.
      const rawTotal = calculateTotalAmount(productAmount, areaHa, "kg/ha");
      const productTotal =
        (rawTotal !== 0 ? rawTotal : productAmount) * replicateMultiplier;

      if (!totalsByProduct[productName]) {
        totalsByProduct[productName] = {
          value: 0,
          measure_unit: productMeasureUnit,
        };
      }

      totalsByProduct[productName].value += productTotal;

      if (nutrientCode) {
        const nutrientPercentage = getFertiliserNutrientPercentage(
          fertiliser,
          nutrientCode
        );

        if (!totalsByNutrient[nutrientCode]) {
          totalsByNutrient[nutrientCode] = {
            value: 0,
            measure_unit: "kg",
          };
        }
        const nutrientAmountPerHa = (productAmount * nutrientPercentage) / 100;
        const rawNutrientTotal = calculateTotalAmount(
          nutrientAmountPerHa,
          areaHa,
          "kg/ha"
        );
        totalsByNutrient[nutrientCode].value +=
          (rawNutrientTotal !== 0 ? rawNutrientTotal : nutrientAmountPerHa) *
          replicateMultiplier;
      }
    });
  });

  return {
    by_product: Object.entries(totalsByProduct).map(([variety, data]) => ({
      variety,
      value: data.value,
      measure_unit: data.measure_unit,
    })),
    by_nutrient: Object.entries(totalsByNutrient).map(([variety, data]) => ({
      variety,
      value: data.value,
      measure_unit: data.measure_unit,
    })),
  };
};

const getBiologicalCalculatedTotals = (
  treatments: any[] = [],
  replicateMultiplier = 1
): {
  by_product: Array<{
    variety: string;
    value: number;
    measure_unit: string;
  }>;
  by_application: Array<
    Array<{ variety: string; value: number; measure_unit: string }>
  >;
} => {
  const totalsByProduct: Record<
    string,
    { value: number; measure_unit: string }
  > = {};

  const byApplication: Array<
    Array<{ variety: string; value: number; measure_unit: string }>
  > = [];

  treatments.forEach((application: any[]) => {
    const appProductTotals: Record<
      string,
      { value: number; measure_unit: string }
    > = {};

    if (Array.isArray(application)) {
      application.forEach((plotTreatment: any) => {
        if (!plotTreatment?.treated) return;
        const products = Array.isArray(plotTreatment?.products)
          ? plotTreatment.products
          : [];

        products.forEach((product: any) => {
          const productName = product?.variety;
          if (!productName) return;

          const measureUnit = product?.measure_unit || "kg/ha";
          const qty = toNumber(product?.quantity) * replicateMultiplier;

          if (!totalsByProduct[productName]) {
            totalsByProduct[productName] = {
              value: 0,
              measure_unit: measureUnit,
            };
          }
          totalsByProduct[productName].value += qty;

          if (!appProductTotals[productName]) {
            appProductTotals[productName] = {
              value: 0,
              measure_unit: measureUnit,
            };
          }
          appProductTotals[productName].value += qty;
        });
      });
    }

    byApplication.push(
      Object.entries(appProductTotals).map(([variety, data]) => ({
        variety,
        value: data.value,
        measure_unit: data.measure_unit,
      }))
    );
  });

  return {
    by_product: Object.entries(totalsByProduct).map(([variety, data]) => ({
      variety,
      value: data.value,
      measure_unit: data.measure_unit,
    })),
    by_application: byApplication,
  };
};

export const addRatesDosagesToPlotProperties = (
  trialData: any,
  protocolData: ProtocolResponse | null
): any => {
  const updatedTrialData = {
    ...trialData,
    trial_plots: trialData?.trial_plots?.map((plot: any) => ({
      ...plot,
      plot: { ...plot.plot },
    })),
  };
  const isStripe = trialData?.stripsData?.isStripMode;

  updatedTrialData?.trial_plots?.forEach((plot: any, index: number) => {
    const replicantsCount = getReplicantCount(plot);
    const replicateMultiplier = 1 + replicantsCount;

    const measureUnit = updatedTrialData?.rates_and_dosages?.unit_of_rate;
    const biologicalMeasureUnit =
      updatedTrialData?.rates_and_dosages
        ?.standard_biological_application_settings?.[0]?.unit;
    const properties =
      updatedTrialData?.rates_and_dosages?.rates_and_dosages[index];
    const savedSeeds = plot.properties?.seeds;
    const savedFertilisers = plot.properties?.fertilisers;
    const savedBiologicals = plot.properties?.biologicals;
    const hasCompleteLocalRates =
      Array.isArray(properties) &&
      properties.length > 0 &&
      properties.length === plot.plot.rows * plot.plot.columns;
    // Missing editor data must not replace saved sections with empty totals.
    const preserveSavedFertilisers =
      savedFertilisers &&
      (!hasCompleteLocalRates ||
        properties.some((property: any) => property?.fertiliser_rate == null));
    const preserveSavedBiologicals =
      savedBiologicals &&
      (!hasCompleteLocalRates ||
        properties.some(
          (property: any) => !Array.isArray(property?.bioTreatement)
        ));
    const hasSeedValues = properties?.some(
      (entry: any) =>
        Number(entry?.rate) > 0 || Boolean(entry?.variety?.trim?.())
    );
    const preserveSavedSeeds =
      (savedSeeds?.rates_and_dosages?.length ?? 0) > 0 &&
      (!hasSeedValues ||
        properties?.length !== plot.plot.rows * plot.plot.columns);
    const seedMeasureUnit =
      measureUnit ||
      protocolData?.product_details?.seeds?.measure_unit ||
      "seeds/ha";

    const updatedProperties = properties?.map(
      (property: any, plotIndex: number) => {
        const areaHa = calculatePlotAreaHa(
          property?.plot_id,
          updatedTrialData?.trial_plots,
          updatedTrialData?.stripsData,
          index
        );
        const rate = toNumber(property?.rate);
        const rawQuantity = calculateTotalAmount(rate, areaHa, seedMeasureUnit);
        const lowerSeedUnit = seedMeasureUnit.toLowerCase();
        const quantity =
          lowerSeedUnit.includes("seeds") && !lowerSeedUnit.includes("kg")
            ? Math.round(rawQuantity)
            : rawQuantity;
        return {
          plot_id: plotIndex + 1,
          color: getCircleColor(
            property.variety,
            property.rate,
            protocolData?.product_details?.seeds?.products,
            trialData?.rates_and_dosages?.unit_of_rate,
            // Pass the whole plot's rate list (not just this one entry) so
            // getCircleColor can see the sibling rates of the same variety and
            // shade by rate — otherwise it always sees a single rate and saves
            // the flat variety base colour, which no longer matches the map
            // (e.g. two strips of one variety saved the same colour).
            properties
          ),
          ...(isStripe ? { is_standard: property?.is_standard ?? false } : {}),
          variety: property.variety,
          rate: property.rate,
          quantity,
          measure_unit: measureUnit,
          product_id: property?.product_id,
        };
      }
    );

    // Pass the original `properties` as rawProperties so that an explicit
    // `quantity` on the input entry is used as fallback when the area-based
    // calculation produces 0 (e.g. plot has no geometry in unit-tests).
    const seedCalculatedTotals = getSeedCalculatedTotals(
      updatedProperties,
      properties,
      seedMeasureUnit,
      replicateMultiplier
    );

    const updatedFertiliserProperties = properties?.map(
      (property: any, plotIndex: number) => {
        const applicationRate = toNumber(property?.fertiliser_rate);
        const standardFertilisers =
          trialData?.standard_field_properties?.standard_fertilisers ??
          trialData?.rates_and_dosages?.standard_fertilisers ??
          [];

        const quantityByProduct = standardFertilisers.reduce(
          (acc: Record<string, number>, fertiliser: any) => {
            const productName = fertiliser?.fertiliser;
            if (!productName) return acc;

            acc[productName] =
              (applicationRate / 100) * toNumber(fertiliser?.rate);
            return acc;
          },
          {}
        );

        const selectedNutrient =
          protocolData?.product_details?.nutrients?.products?.[0]
            ?.nutrient_code ?? "";
        const rawNutrientTotals = selectedNutrient
          ? standardFertilisers.reduce(
              (acc: Record<string, number>, fertiliser: any) => {
                const nutrientPercentage = getFertiliserNutrientPercentage(
                  fertiliser,
                  selectedNutrient
                );
                const fertiliserAmount =
                  (applicationRate / 100) * toNumber(fertiliser?.rate);

                acc[selectedNutrient] =
                  (acc[selectedNutrient] || 0) +
                  (fertiliserAmount * nutrientPercentage) / 100;

                return acc;
              },
              {}
            )
          : {};

        const quantityByNutrient = selectedNutrient
          ? Object.fromEntries(
              Object.entries(rawNutrientTotals).map(([k, v]) => [
                k,
                toNumber(v),
              ])
            )
          : {};

        return {
          plot_id: plotIndex + 1,
          color:
            property?.mapFillColor ??
            getCircleColorNutrient(
              // The plot's own rate list (each entry has fertiliser_rate) and the
              // plot-group index, matching the map's display call. Passing
              // standard_fertilisers gave rates of 0 for every cell, so every cell
              // was saved the same flat colour instead of a per-rate shade.
              properties,
              property?.fertiliser_rate,
              undefined,
              index
            ),
          rate: applicationRate,
          amount: toNumber(property?.fertiliser_amount),
          quantity_by_product: quantityByProduct,
          quantity_by_nutrient: quantityByNutrient,
        };
      }
    );

    const selectedNutrient =
      protocolData?.product_details?.nutrients?.products?.[0]?.nutrient_code ??
      "";
    const standardFertilisers =
      trialData?.standard_field_properties?.standard_fertilisers ??
      trialData?.rates_and_dosages?.standard_fertilisers ??
      [];
    const fertiliserCalculatedTotals = getFertiliserCalculatedTotals(
      updatedFertiliserProperties,
      standardFertilisers,
      selectedNutrient,
      updatedTrialData?.trial_plots,
      updatedTrialData?.stripsData,
      index,
      replicateMultiplier
    );

    const updatedBiologicalProperties = properties?.map((property: any) => {
      const bioTreatments = Array.isArray(property?.bioTreatement)
        ? property?.bioTreatement
        : [];
      const areaHa = calculatePlotAreaHa(
        property?.plot_id,
        updatedTrialData?.trial_plots,
        updatedTrialData?.stripsData,
        index
      );
      return bioTreatments?.map((treatment: any) => ({
        ...treatment,
        treated: treatment?.treated || false,
        selected_treatment: Array.isArray(treatment?.selected_treatment)
          ? treatment?.selected_treatment.filter(Boolean)
          : [],
        products: (() => {
          const mapProduct = (product: any): Record<string, unknown> => {
            const productRate = toNumber(product?.rate);
            const productMeasureUnit =
              product?.measure_unit || biologicalMeasureUnit || "kg/ha";
            const computedQty = calculateTotalAmount(
              productRate,
              areaHa,
              productMeasureUnit
            );
            // Fall back to the raw quantity on the product when area is 0
            // so that unit-tests supplying an explicit quantity still pass.
            const quantity =
              computedQty !== 0 ? computedQty : toNumber(product?.quantity);
            return {
              ...product,
              rate: productRate,
              quantity,
            };
          };

          if (
            Array.isArray(treatment?.products) &&
            treatment.products.length > 0
          ) {
            return treatment.products.map(mapProduct);
          }

          if (!treatment?.treated) return [];
          const selectedTreatmentIds = Array.isArray(
            treatment?.selected_treatment
          )
            ? treatment.selected_treatment
            : [];
          return selectedTreatmentIds.flatMap((treatmentId: number) => {
            const protocolTreatment =
              protocolData?.product_details?.biologicals?.treatments?.[
                treatmentId - 1
              ];
            return Array.isArray(protocolTreatment?.products)
              ? (protocolTreatment?.products ?? []).map(mapProduct)
              : [];
          });
        })(),
        color: getCircleColorBiologicals(
          treatment,
          treatment?.selected_treatment?.[0] ?? null
        ),
      }));
    });

    const transformedBiologicalProperties = (() => {
      if (!updatedBiologicalProperties?.length) {
        return [];
      }

      const maxApplications = Math.max(
        ...updatedBiologicalProperties.map((plotTreatments: any) =>
          Array.isArray(plotTreatments) ? plotTreatments.length : 0
        ),
        0
      );

      const blankTreatment = (
        appIndex: number
      ): {
        application_index: number;
        rate: number;
        selected_treatment: any[];
        treated: boolean;
      } => ({
        application_index: appIndex,
        rate: 0,
        selected_treatment: [],
        treated: false,
      });

      const treatments = [];
      if (maxApplications !== 0) {
        for (let appIndex = 0; appIndex < maxApplications; appIndex++) {
          const applicationTreatments = [];
          for (
            let plotIndex = 0;
            plotIndex < updatedBiologicalProperties.length;
            plotIndex++
          ) {
            const plotTreatments = updatedBiologicalProperties[plotIndex];
            const treatment =
              Array.isArray(plotTreatments) && plotTreatments[appIndex]
                ? plotTreatments[appIndex]
                : {
                    treated: false,
                    selected_treatment: [],
                    rate: 0,
                    application_index: appIndex,
                  };
            applicationTreatments.push(treatment);
          }
          treatments.push(applicationTreatments);
        }
      } else {
        for (
          let plotIndex = 0;
          plotIndex < updatedBiologicalProperties.length;
          plotIndex++
        ) {
          treatments.push(blankTreatment(0));
        }
        return [treatments];
      }
      return treatments;
    })();

    plot.plot.plot_id = plot.plot.id;
    plot.properties = {};

    if (protocolData?.protocol_type?.includes("seed")) {
      plot.properties.seeds = preserveSavedSeeds
        ? savedSeeds
        : {
            measure_unit:
              measureUnit ||
              protocolData?.product_details?.seeds?.measure_unit ||
              "seeds/ha",
            rates_and_dosages: updatedProperties,
            calculated_totals: seedCalculatedTotals,
          };
    }
    if (protocolData?.protocol_type?.includes("nutrient")) {
      plot.properties.fertilisers = preserveSavedFertilisers
        ? savedFertilisers
        : {
            measure_unit: "kg/ha",
            rates_and_dosages: updatedFertiliserProperties,
            calculated_totals: fertiliserCalculatedTotals,
          };
    }

    if (protocolData?.protocol_type?.includes("biological")) {
      plot.properties.biologicals = preserveSavedBiologicals
        ? savedBiologicals
        : {
            measure_unit:
              biologicalMeasureUnit ||
              trialData?.standard_field_properties
                ?.standard_biological_application_settings?.[0]?.rate,
            rate: toNumber(
              trialData?.rates_and_dosages?.standard_tank_mix_rates?.[0] ??
                trialData?.standard_field_properties
                  ?.standard_tank_mix_rates?.[0]
            ),
            treatments: transformedBiologicalProperties || [],
            calculated_totals: getBiologicalCalculatedTotals(
              transformedBiologicalProperties,
              replicateMultiplier
            ),
          };
    }
  });

  return updatedTrialData;
};
